import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, CheckCircle2, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Reveal } from "@/components/Section";
import { HeroBanner } from "@/components/Section";
import { getSupportCategories, submitSupport } from "@/lib/api";
import { formatApiError } from "@/lib/cms";
import { useContent } from "@/context/ContentContext";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  company: "",
  category: "",
  subject: "",
  message: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (form) => {
  const errors = {};
  if (form.name.trim().length < 2) errors.name = "Name must be at least 2 characters";
  if (!EMAIL_RE.test(form.email.trim())) errors.email = "Enter a valid email address";
  if (form.phone.trim().length < 4) errors.phone = "Phone number is too short";
  if (form.company.trim().length > 160) errors.company = "Company name is too long";
  if (!form.category) errors.category = "Please select a category";
  if (form.subject.trim().length < 2) errors.subject = "Subject must be at least 2 characters";
  if (form.message.trim().length < 5) errors.message = "Message must be at least 5 characters";
  return errors;
};

export default function Support() {
  const { content } = useContent();
  const COMPANY = content.company || {};
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    getSupportCategories().then((c) => {
      setCategories(c);
      const preset = searchParams.get("category");
      if (preset && c.includes(preset)) setForm((f) => ({ ...f, category: preset }));
    });
  }, [searchParams]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      toast.error(Object.values(fieldErrors)[0]);
      return;
    }
    setLoading(true);
    try {
      await submitSupport(form);
      setDone(true);
      setForm(EMPTY);
      setErrors({});
      toast.success("Your request has been submitted. We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err, "Submission failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="support-page">
      <section className="relative overflow-hidden border-b border-border carbon-texture">
        <HeroBanner src={content.page_banners?.support} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-secondary">
            {content.page_headers?.support_eyebrow || "We're Here To Help"}
          </span>
          <h1 className="mt-4 font-heading text-5xl font-bold tracking-tight text-white md:text-6xl">
            {content.page_headers?.support_title || "Support & Contact"}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-white/70">
            {content.page_headers?.support_description || "Product inquiries, dealership requests, technical support or warranty claims — send us a message and our team will respond promptly."}
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:py-20 lg:grid-cols-5 lg:px-8">
        {/* Contact info */}
        <Reveal className="lg:col-span-2">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
            Get In Touch
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Reach out to Runner Trade Park Limited through the channels below or fill out the form.
          </p>
          <ul className="mt-8 space-y-6">
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-primary text-white">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Address</div>
                <p className="mt-1 text-sm text-foreground">{COMPANY.address}</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-primary text-white">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Phone</div>
                <p className="mt-1 text-sm text-foreground">{COMPANY.phone}</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-primary text-white">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Email</div>
                <p className="mt-1 text-sm text-foreground">{COMPANY.email}</p>
              </div>
            </li>
          </ul>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1} className="lg:col-span-3">
          <div className="border border-border bg-card p-6 md:p-10">
            {done ? (
              <div className="flex flex-col items-center py-16 text-center" data-testid="support-success">
                <CheckCircle2 className="h-16 w-16 text-primary" />
                <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-foreground">
                  Request Received
                </h3>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                  Thank you for contacting RTPL. Our team will review your message and get back to
                  you shortly.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="support-new-btn"
                  className="mt-8 border-2 border-primary px-6 py-2.5 font-heading text-sm font-bold tracking-wide text-primary transition-colors hover:bg-primary hover:text-white"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="support-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="name" className="text-xs font-bold tracking-wide">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="support-name"
                      required
                      maxLength={120}
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="mt-2 rounded-xl border-2"
                      placeholder="Your name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs text-destructive" data-testid="support-name-error">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs font-bold tracking-wide">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="support-email"
                      required
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="mt-2 rounded-xl border-2"
                      placeholder="you@example.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-destructive" data-testid="support-email-error">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs font-bold tracking-wide">
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      data-testid="support-phone"
                      required
                      maxLength={40}
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className="mt-2 rounded-xl border-2"
                      placeholder="+880 1XXX-XXXXXX"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-xs text-destructive" data-testid="support-phone-error">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-xs font-bold tracking-wide">
                      Company (optional)
                    </Label>
                    <Input
                      id="company"
                      data-testid="support-company"
                      maxLength={160}
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      className="mt-2 rounded-xl border-2"
                      placeholder="Business name"
                    />
                    {errors.company && (
                      <p className="mt-1 text-xs text-destructive" data-testid="support-company-error">
                        {errors.company}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-bold tracking-wide">Category *</Label>
                  <Select value={form.category} onValueChange={(v) => update("category", v)}>
                    <SelectTrigger
                      data-testid="support-category"
                      className="mt-2 rounded-xl border-2"
                    >
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`category-option-${c.replace(/\s/g, "-").toLowerCase()}`}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category && (
                    <p className="mt-1 text-xs text-destructive" data-testid="support-category-error">
                      {errors.category}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="subject" className="text-xs font-bold tracking-wide">
                    Subject *
                  </Label>
                  <Input
                    id="subject"
                    data-testid="support-subject"
                    required
                    maxLength={200}
                    value={form.subject}
                    onChange={(e) => update("subject", e.target.value)}
                    className="mt-2 rounded-xl border-2"
                    placeholder="How can we help?"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-xs text-destructive" data-testid="support-subject-error">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs font-bold tracking-wide">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="support-message"
                    required
                    rows={5}
                    maxLength={4000}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="mt-2 rounded-xl border-2"
                    placeholder="Describe your inquiry in detail..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-xs text-destructive" data-testid="support-message-error">
                      {errors.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="support-submit-btn"
                  className="flex w-full items-center justify-center gap-2 bg-primary px-6 py-3.5 font-heading text-sm font-bold tracking-wide text-white transition-colors hover:bg-primary/90 shadow-md hover:shadow-lg disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
